import React, { useContext, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AnDateField from '../../../../components/DateTime/DateField'
import { GlobalContext, UserContext } from '../../../../App'
import { Avatar } from '@mui/material'
import validator from 'validator'
import imageConverter from '../../../../helpers/imageConverter'
import { MOBILE_LOCALES } from '../../../../helpers/region'
import API from '../../../../services/API'

const PersonalDetails = ({ accountData, setAccountData }) => {
  const [phoneNumberError, setPhoneNumberError] = useState('')
  function isValidMobile(mobile) {
    return validator.isMobilePhone(
      String(mobile ?? '').replace(/\s+/g, ''),
      MOBILE_LOCALES,
    )
  }

  const { user, setUser } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)

  const fileInput = useRef()

  const handlePhotoChange = async (e) => {
    try {
      const file = e.target.files[0]
      const image = await imageConverter(file)
      API.put('/employee/photo', {
        employeeID: user._id,
        image,
      }).then((res) => {
        if (res.status === 200) {
          setUser((prev) => ({
            ...prev,
            photograph: URL.createObjectURL(file),
          }))
        }
        setToast({ message: 'Image Updated' })
      })
    } catch (e) {
      console.error(e)
      setToast({
        severity: 'error',
        message: 'Unable to upload image. Please try again later.',
      })
    }
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={3} lg={2}>
          <TextField
            value={accountData?.prefix ?? ''}
            label="Prefix"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                prefix: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={9} lg={5}>
          <TextField
            value={accountData?.firstName ?? ''}
            label="First Name"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} lg={5}>
          <TextField
            value={accountData?.lastName ?? ''}
            label="Last Name"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <AnDateField
            date={accountData?.employee?.personnelInfo?.dob ?? null}
            onChange={(newValue) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    dob: newValue,
                  },
                },
              }))
            }
            label="Date of Birth"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            value={
              accountData?.employee?.personnelInfo?.nationalInsurance ?? ''
            }
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    nationalInsurance: e.target.value,
                  },
                },
              }))
            }
            label="National Insurance Number"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            value={accountData?.email ?? ''}
            disabled
            label="Email Address"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            value={accountData?.employee?.personnelInfo?.phoneNumber1 ?? ''}
            label="Mobile Phone Number"
            error={!!phoneNumberError}
            helperText={phoneNumberError || 'Used for SMS Notifications'}
            fullWidth
            onChange={(e) => {
              const value = e.target.value
              if (!isValidMobile(value)) {
                setPhoneNumberError('Please enter a valid UK or Irish mobile number')
              } else {
                setPhoneNumberError('')
              }

              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    phoneNumber1: value,
                  },
                },
              }))
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="title3">Address</Typography>
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Street Address Line 1"
            value={accountData?.employee?.personnelInfo?.address?.lineOne ?? ''}
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    address: {
                      ...prev.employee?.personnelInfo?.address,
                      lineOne: e.target.value,
                    },
                  },
                },
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Street Address Line 2"
            value={accountData?.employee?.personnelInfo?.address?.lineTwo ?? ''}
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    address: {
                      ...prev.employee?.personnelInfo?.address,
                      lineTwo: e.target.value,
                    },
                  },
                },
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Town"
            value={accountData?.employee?.personnelInfo?.address?.town ?? ''}
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    address: {
                      ...prev.employee?.personnelInfo?.address,
                      town: e.target.value,
                    },
                  },
                },
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="City"
            value={accountData?.employee?.personnelInfo?.address?.city ?? ''}
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    address: {
                      ...prev.employee?.personnelInfo?.address,
                      city: e.target.value,
                    },
                  },
                },
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="County"
            value={accountData?.employee?.personnelInfo?.address?.county ?? ''}
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    address: {
                      ...prev.employee?.personnelInfo?.address,
                      county: e.target.value,
                    },
                  },
                },
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Post Code"
            value={
              accountData?.employee?.personnelInfo?.address?.postCode ?? ''
            }
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    address: {
                      ...prev.employee?.personnelInfo?.address,
                      postCode: e.target.value,
                    },
                  },
                },
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="title3">Emergency Contact</Typography>
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            value={
              accountData?.employee?.personnelInfo?.emergencyContact
                ?.firstName ?? ''
            }
            label="First Name"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    emergencyContact: {
                      ...prev.employee?.personnelInfo?.emergencyContact,
                      firstName: e.target.value,
                    },
                  },
                },
              }))
            }
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            value={
              accountData?.employee?.personnelInfo?.emergencyContact
                ?.lastName ?? ''
            }
            label="Last Name"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    emergencyContact: {
                      ...prev.employee?.personnelInfo?.emergencyContact,
                      lastName: e.target.value,
                    },
                  },
                },
              }))
            }
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            value={
              accountData?.employee?.personnelInfo?.emergencyContact
                ?.relationship ?? ''
            }
            label="Relationship"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    emergencyContact: {
                      ...prev.employee?.personnelInfo?.emergencyContact,
                      relationship: e.target.value,
                    },
                  },
                },
              }))
            }
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            value={
              accountData?.employee?.personnelInfo?.emergencyContact?.phone1 ??
              ''
            }
            label="Phone 1"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    emergencyContact: {
                      ...prev.employee?.personnelInfo?.emergencyContact,
                      phone1: e.target.value,
                    },
                  },
                },
              }))
            }
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            value={
              accountData?.employee?.personnelInfo?.emergencyContact?.phone2 ??
              ''
            }
            label="Phone 2"
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    emergencyContact: {
                      ...prev.employee?.personnelInfo?.emergencyContact,
                      phone2: e.target.value,
                    },
                  },
                },
              }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="title3">Driving Licence</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Driving Licence No"
            value={
              accountData?.employee?.personnelInfo?.driving?.licenceNumber ?? ''
            }
            disabled={!!user?.employee?.personnelInfo?.driving?.licenceNumber}
            fullWidth
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  personnelInfo: {
                    ...prev.employee?.personnelInfo,
                    driving: {
                      ...prev.employee?.personnelInfo?.driving,
                      licenceNumber: e.target.value,
                    },
                  },
                },
              }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="title3">Photograph</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          alignItems="center"
          justifyContent="center"
          // rowGap={2}
          display="flex"
          flexDirection="column"
          sx={{ cursor: 'pointer' }}
        >
          <Avatar
            src={user.photograph}
            sx={{
              height: 200,
              width: 200,
              borderRadius: '5px 5px 0 0',
            }}
          />
          <input
            ref={fileInput}
            type="file"
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handlePhotoChange}
          />
          <Button
            sx={{ width: 200, borderRadius: '0px 0px 5px 5px' }}
            onClick={() => fileInput.current.click()}
            variant="contained"
          >
            Change ID Photo
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Staff ID / PIN"
            disabled
            value={accountData?.employee?.pinNumber}
            onChange={(e) =>
              setAccountData((prev) => ({
                ...prev,
                employee: {
                  ...prev.employee,
                  pinNumber: e.target.value,
                },
              }))
            }
            fullWidth
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default PersonalDetails
